import React, { useCallback, useEffect, useRef, useState } from "react";
import { executeCapabilityAction, fetchCapabilityControlSurface } from "../api/client";
import {
  describeCapabilityStatus,
  availablePlaylists,
  emptyActionsNote,
  isAudioCapability,
  resolvePlaylistSelection,
  visibleActions,
} from "../capabilityControls";
import DigitalTwinCanvas from "./DigitalTwinCanvas";
import SpaceNavigator from "./SpaceNavigator";
import { buildSpaceTree, flattenSpaces, getSpaceById, getSpaceCapabilities } from "../spatialModel";

// Operator-visible state (playing, light on/off) can change outside this panel
// (another operator, a class flow), so re-read it periodically while the tab is visible.
const REFRESH_INTERVAL_MS = 10000;

function describeAvailabilityReason(reason) {
  if (!reason) return "Unavailable";
  return String(reason)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export default function ReceptionistCapabilityControls({
  spaces: spaceMap,
  capabilityCatalog,
  loadControlSurface = fetchCapabilityControlSurface,
  executeAction = executeCapabilityAction,
  onScheduleClass,
  scheduleFormOpen = false,
  onCheckIn,
}) {
  const spaces = buildSpaceTree(spaceMap);
  const flattenedSpaces = flattenSpaces(spaces);
  const workflowSpace = flattenedSpaces.find((space) => space.capabilities.some((id) => ["scheduling", "check_in"].includes(capabilityCatalog[id]?.type)));
  const firstCapableSpace = workflowSpace || flattenedSpaces.find((space) => space.capabilities.length > 0);
  const [selectedSpaceId, setSelectedSpaceId] = useState(firstCapableSpace?.id || flattenedSpaces[0]?.id || "");
  const [controls, setControls] = useState([]);
  const [controlsStatus, setControlsStatus] = useState("loading"); // loading | ready | error
  const [selectedPlaylists, setSelectedPlaylists] = useState({});
  const [loadError, setLoadError] = useState("");
  const [pendingAction, setPendingAction] = useState("");
  const [actionMessage, setActionMessage] = useState("");

  const resolvedSelectedSpaceId = flattenedSpaces.some((space) => space.id === selectedSpaceId)
    ? selectedSpaceId
    : (firstCapableSpace?.id || flattenedSpaces[0]?.id || "");

  useEffect(() => {
    if (selectedSpaceId !== resolvedSelectedSpaceId) {
      setSelectedSpaceId(resolvedSelectedSpaceId);
    }
  }, [resolvedSelectedSpaceId, selectedSpaceId]);

  const selectedSpace = getSpaceById(spaces, resolvedSelectedSpaceId) || firstCapableSpace || flattenedSpaces[0] || null;
  // A result message belongs to the room it came from, so drop it when the operator moves on.
  const selectSpace = (spaceId) => {
    setSelectedSpaceId(spaceId);
    setActionMessage("");
  };

  // Only the newest response may update state, so a slow poll can never overwrite a fresher read.
  const requestSeq = useRef(0);
  const pendingRef = useRef("");
  pendingRef.current = pendingAction;

  const refreshControls = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      const payload = await loadControlSurface();
      if (seq !== requestSeq.current) return;
      setControls(payload.capabilities || []);
      setControlsStatus("ready");
      setLoadError("");
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setControlsStatus((current) => (current === "ready" ? current : "error"));
      setLoadError(error.message || "Could not load the capability controls.");
    }
  }, [loadControlSurface]);

  useEffect(() => {
    refreshControls();
    const refreshIfIdle = () => {
      if (document.visibilityState === "visible" && !pendingRef.current) refreshControls();
    };
    const timer = window.setInterval(refreshIfIdle, REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", refreshIfIdle);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refreshIfIdle);
    };
  }, [refreshControls]);

  // Before the control surface loads, still show the configured name for each capability.
  const catalogEntries = new Map(getSpaceCapabilities(selectedSpace, capabilityCatalog).map((entry) => [entry.id, entry]));
  const selectedCapabilities = (selectedSpace?.capabilities || []).map((id) => {
    const control = controls.find((item) => item.id === id);
    if (control) return control;
    const entry = catalogEntries.get(id);
    return {
      id,
      type: entry?.type || "unknown",
      label: entry?.label || "Capability",
      connection: "UNKNOWN",
      state: "UNKNOWN",
      actions: [],
    };
  });

  const performAction = async (capability, action) => {
    if (action.execution === "workflow") {
      const callback = action.target === "schedule" ? onScheduleClass : onCheckIn;
      callback?.();
      setActionMessage(action.target === "schedule" ? "" : "Check In workflow opened.");
      return;
    }

    const actionId = action.id;
    setPendingAction(`${capability.id}:${actionId}`);
    setActionMessage("");
    try {
      const playlistId = resolvePlaylistSelection(capability, selectedPlaylists[capability.id]);
      const result = await executeAction(
        capability.id,
        actionId,
        // Send exactly what the dropdown shows.
        actionId === "play" ? { playlist_id: playlistId } : {},
      );
      if (!result.success) {
        setActionMessage(result.selection_status === "PERSISTED"
          ? "Playlist selection was saved, but playback did not start."
          : `${result.action} request did not complete.`);
      }
    } catch (error) {
      setActionMessage(error.result?.selection_status === "PERSISTED"
        ? "Playlist selection was saved, but playback did not start."
        : error.result?.code === "AUDIO_SELECTION_PERSISTENCE_FAILED"
          ? "Playlist selection could not be saved; playback was not started."
          : error.message);
    } finally {
      setPendingAction("");
      // Re-read on success AND failure: a rejected action usually means what is on screen is stale.
      refreshControls();
    }
  };

  const selectPlaylist = async (capability, playlistId) => {
    setSelectedPlaylists((current) => ({ ...current, [capability.id]: playlistId }));
    setPendingAction(`${capability.id}:select_playlist`);
    setActionMessage("");
    try {
      const result = await executeAction(capability.id, "select_playlist", {
        playlist_id: playlistId,
      });
      setActionMessage(result.success
        ? "Playlist selection saved. Playback was not started."
        : "Playlist selection could not be saved.");
    } catch (error) {
      setActionMessage(error.message || "Playlist selection could not be saved.");
    } finally {
      setSelectedPlaylists((current) => {
        const updated = { ...current };
        delete updated[capability.id];
        return updated;
      });
      setPendingAction("");
      refreshControls();
    }
  };

  const selectPlaybackMode = async (capability, mode) => {
    setPendingAction(`${capability.id}:set_programme_mode`);
    setActionMessage("");
    try {
      const result = await executeAction(
        capability.id,
        "set_programme_mode",
        { mode },
      );
      if (!result.success) {
        setActionMessage(result.error || "Playback mode could not be saved and activated for every endpoint.");
      } else if (mode === "auto") {
        const activationOutcomes = (result.endpoints || [])
          .map((endpoint) => endpoint.activation?.outcome)
          .filter(Boolean);
        if (activationOutcomes.length && activationOutcomes.every((outcome) => outcome === "accepted")) {
          setActionMessage(
            "Automatic mode was saved and the backend accepted the programme launch. Endpoint playback remains reported separately.",
          );
        } else if (activationOutcomes.some((outcome) => outcome === "deferred")) {
          setActionMessage(
            "Automatic mode was saved; programme playback is deferred by room arbitration. Playback state is reported separately.",
          );
        } else {
          setActionMessage(
            "Automatic mode was saved. The current endpoint playback state is reported separately.",
          );
        }
      } else {
        setActionMessage("Manual mode was saved. Current playback was not changed.");
      }
    } catch (error) {
      setActionMessage(error.result?.error || error.message || "Playback mode could not be changed.");
    } finally {
      setPendingAction("");
      refreshControls();
    }
  };

  return (
    <section className="receptionist-control-surface" aria-labelledby="receptionist-controls-title">
      <header className="receptionist-control-heading">
        <h2 id="receptionist-controls-title">Studio spaces</h2>
        <p>Select a space to see its logical capabilities and available controls.</p>
      </header>
      <div className="receptionist-control-layout">
        <DigitalTwinCanvas
          spaces={spaces}
          capabilities={capabilityCatalog}
          selectedSpaceId={selectedSpace?.id || ""}
          onSelect={selectSpace}
          showHeading={false}
        />
        <aside className="receptionist-control-sidebar">
          <SpaceNavigator
            spaces={spaces}
            selectedSpaceId={selectedSpace?.id || ""}
            onSelect={selectSpace}
            showAccountAction={false}
          />
          <section className="receptionist-controls-panel" aria-label="Selected space controls">
            <h3>{selectedSpace ? `${selectedSpace.label} capabilities` : "Capabilities"}</h3>
            {controlsStatus === "error" && (
              <p className="studio-action-error" role="alert">
                {loadError}{" "}
                <button className="studio-btn-xs" type="button" onClick={refreshControls}>Retry</button>
              </p>
            )}
            {selectedCapabilities.map((capability) => {
              const status = describeCapabilityStatus(capability, controlsStatus);
              const allActions = visibleActions(capability, controlsStatus);
              const note = emptyActionsNote(capability, controlsStatus);
              const playlistId = isAudioCapability(capability)
                ? resolvePlaylistSelection(capability, selectedPlaylists[capability.id])
                : "";
              const manualPlaylists = isAudioCapability(capability)
                ? availablePlaylists(capability)
                : [];
              const selectedPlaylistAvailable = Boolean(playlistId && manualPlaylists.some(
                (item) => item.id === playlistId && item.available,
              ));
              const selectionEnabled = Boolean(capability.actions?.some(
                (action) => action.id === "select_playlist" && action.enabled,
              ));
              const autoProgramme = capability.auto_programme;
              const operatorMode = autoProgramme?.operator_mode || "manual";
              const automaticSelected = operatorMode === "auto";
              const modeActionEnabled = Boolean(allActions.some(
                (action) => action.id === "set_programme_mode" && action.enabled,
              ));
              const actions = allActions.filter((action) => (
                action.id !== "set_programme_mode"
                && (!automaticSelected || action.id === "stop")
              ));
              const playbackLabel = {
                PLAYING: "Playing",
                STOPPED: "Stopped",
                UNAVAILABLE: "Unavailable",
                UNKNOWN: "Unknown",
              }[autoProgramme?.playback_state] || "Unknown";
              return (
                <article className="receptionist-capability" key={capability.id}>
                  <div className="receptionist-capability-heading">
                    <strong>{capability.label}</strong>
                    <span className={`receptionist-capability-status is-${status.tone}`} title={status.hint || undefined}>
                      {status.label}
                    </span>
                  </div>
                  {isAudioCapability(capability) && autoProgramme && (
                    <section className="receptionist-auto-programme" aria-label="Audio playback">
                      <p>
                        Mode: {automaticSelected ? "Automatic programme" : operatorMode === "manual" ? "Manual" : "Mixed or unknown"}
                        {" · "}
                        Playback: {playbackLabel}
                      </p>
                      {modeActionEnabled && (
                        <label className="studio-schedule-field">
                          <span>Playback mode</span>
                          <select
                            aria-label="Audio playback mode"
                            value={operatorMode === "manual" || operatorMode === "auto" ? operatorMode : ""}
                            disabled={Boolean(pendingAction)}
                            onChange={(event) => selectPlaybackMode(capability, event.target.value)}
                          >
                            {operatorMode === "mixed" && <option value="">Choose a mode</option>}
                            <option value="manual">Manual</option>
                            <option value="auto" disabled={!autoProgramme.activation_available}>
                              Automatic programme
                            </option>
                          </select>
                        </label>
                      )}
                      {automaticSelected && (
                        <p>
                          {autoProgramme.activation_blocker
                            ? autoProgramme.activation_blocker
                            : autoProgramme.desired_stage_label
                              ? `Scheduled stage: ${autoProgramme.desired_stage_label}. Playback is reported separately.`
                              : "No programme stage is currently scheduled."}
                        </p>
                      )}
                      {!automaticSelected && autoProgramme.activation_blocker && (
                        <p className="studio-audio-activation-blocker" role="status">
                          {autoProgramme.activation_blocker}
                        </p>
                      )}
                      {autoProgramme.diagnostics && (
                        <details className="receptionist-audio-diagnostics">
                          <summary>Audio diagnostics</summary>
                          <p>
                            Scheduler configured: {autoProgramme.diagnostics.scheduler_configured_enabled ? "Enabled" : "Disabled"}
                            {" · "}
                            Runtime: {describeAvailabilityReason(autoProgramme.diagnostics.scheduler_runtime_status)}
                            {" · "}
                            Stage catalogue: {autoProgramme.diagnostics.configuration_valid ? "Valid" : "Needs attention"}
                          </p>
                          {autoProgramme.diagnostics.endpoints?.map((endpoint) => (
                            <p key={endpoint.id}>
                              {endpoint.id}: mode {endpoint.mode}; last recorded {endpoint.last_recorded_status};
                              {" "}endpoint readback {endpoint.endpoint_playback_state_verified
                                ? describeAvailabilityReason(endpoint.endpoint_playback_state)
                                : "Unknown"};
                              {" "}binding {describeAvailabilityReason(endpoint.binding_state)};
                              {" "}available {endpoint.endpoint_available ? "yes" : "no"};
                              {" "}identity verified {endpoint.endpoint_verified ? "yes" : "no"};
                              {" "}last stage {endpoint.last_recorded_stage_id || "None"};
                              {" "}desired stage {endpoint.desired_stage_id || "None"}
                            </p>
                          ))}
                          <ul aria-label="Automatic programme stage diagnostics">
                            {(autoProgramme.diagnostics.stages || []).map((stage) => (
                              <li key={stage.id}>
                                {stage.id}: {stage.available
                                  ? stage.warning
                                    ? `Available · ${describeAvailabilityReason(stage.warning)}`
                                    : "Available"
                                  : describeAvailabilityReason(stage.reason)}
                              </li>
                            ))}
                          </ul>
                          <ul aria-label="Manual playlist diagnostics">
                            {(autoProgramme.diagnostics.manual_playlists || []).map((playlist) => (
                              <li key={playlist.id}>
                                {playlist.id}: {playlist.available
                                  ? playlist.warning
                                    ? `Available · ${describeAvailabilityReason(playlist.warning)}`
                                    : "Available"
                                  : describeAvailabilityReason(playlist.reason)}
                                {playlist.rejected_entry
                                  ? ` — rejected ${playlist.rejected_entry} (${playlist.detected_format})`
                                  : ""}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </section>
                  )}
                  {isAudioCapability(capability) && capability.playlist_catalog_unavailable_reason === "MEDIA_SOURCE_UNAVAILABLE" && (
                    <p className="studio-muted" role="status">
                      Playlists are unavailable because the media source cannot be accessed.
                    </p>
                  )}
                  {isAudioCapability(capability) && !automaticSelected && controlsStatus === "ready" && (capability.connection === "AVAILABLE" || selectionEnabled) && (
                    <label className="studio-schedule-field receptionist-audio-playlist">
                      <span>Playlist</span>
                      {capability.selection_conflict && (
                        <span role="status">Endpoint selections differ. Choose one playlist to apply to this space.</span>
                      )}
                      <select
                        value={selectedPlaylistAvailable ? playlistId : ""}
                        disabled={Boolean(pendingAction) || !selectionEnabled}
                        onChange={(event) => selectPlaylist(capability, event.target.value)}
                      >
                        <option value="" disabled>Select playlist</option>
                        {manualPlaylists.map((playlist) => (
                          <option key={playlist.id} value={playlist.id}>
                            {playlist.warning
                              ? `${playlist.label} · ${describeAvailabilityReason(playlist.warning)}`
                              : playlist.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  {actions.length > 0 && (
                    <div className="receptionist-capability-actions">
                      {actions.map((action) => (
                        <button
                          className="studio-btn-secondary"
                          type="button"
                          key={action.id}
                          disabled={
                            !action.enabled
                            || Boolean(pendingAction)
                            || (action.id === "play" && !selectedPlaylistAvailable)
                          }
                          aria-expanded={action.target === "schedule" ? scheduleFormOpen : undefined}
                          onClick={() => performAction(capability, action)}
                        >
                          {pendingAction === `${capability.id}:${action.id}`
                            ? "Working…"
                            : action.target === "schedule"
                              ? scheduleFormOpen ? "Close" : "Schedule"
                              : action.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {actions.length === 0 && note && <p className="studio-muted">{note}</p>}
                </article>
              );
            })}
            {actionMessage && <p className="studio-muted" role="status">{actionMessage}</p>}
          </section>
        </aside>
      </div>
    </section>
  );
}
